import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
  CONDITION_OPS,
  ConditionOp,
  ConditionOpDef,
  FilterCondition,
  FilterFieldDef,
  FilterJoinMode,
  FilterOption,
  completeConditions,
  describeCondition,
  describeConditions,
  fieldById,
  isConditionComplete,
  newConditionId,
  optionsForField,
} from '../../scoring-leader-utils/recordFilterEngine';
import {
  FilterPresetStore,
  SavedFilterView,
  describeAppliedView,
  removeSavedFilterView,
  resolveSavedFilterView,
  upsertSavedFilterView,
} from '../../scoring-leader-utils/filterPresetStore';

export interface RecordFilterItemOption {
  id: number;
  label: string;
}

export interface RecordFilterChange {
  conditions: FilterCondition[];
  joinMode: FilterJoinMode;
}

export interface RecordFilterViewApplied extends RecordFilterChange {
  view: SavedFilterView;
  droppedItemIds: number[];
  droppedFieldIds: string[];
}

/**
 * Notion-style filter bar over the record-filter engine: one chip per condition, a popover
 * editor per chip, an advanced panel with the And/Or join, and (when a preset store is given)
 * a saved-filters popover. The bar never filters rows — the host runs its own stack on
 * `filterChange` — so the same component serves surfaces with very different catalogues.
 *
 * Conditions are edited in place and re-emitted; the host keeps the array.
 */
@Component({
  selector: 'record-filter-bar',
  templateUrl: './record-filter-bar.component.html',
  styleUrls: ['./record-filter-bar.component.scss'],
})
export class RecordFilterBarComponent implements OnChanges {

  /** Field catalogue for this surface. */
  @Input() fields: FilterFieldDef[] = [];
  @Input() conditions: FilterCondition[] = [];
  @Input() joinMode: FilterJoinMode = 'AND';
  /** Context handed to `optionsOf` for enum fields (e.g. the loaded items). */
  @Input() ctx: any = {};
  /** Choices for `items`-type fields. */
  @Input() itemOptions: RecordFilterItemOption[] = [];

  /** Saved views. Omit to hide the Saved filters control. */
  @Input() presetStore: FilterPresetStore | null = null;
  /** Surface key recorded on saved views (informational). */
  @Input() surface = '';
  /** Host-owned state stored with a view (e.g. column-group toggle ids). */
  @Input() extraState: string[] = [];
  /** Item ids in scope, used to trim item conditions when a view is applied. */
  @Input() availableItemIds: number[] | null = null;

  /** True when something outside the bar (e.g. an ag-Grid column filter) is narrowing rows. */
  @Input() hasExternalFilter = false;

  @Input() rowLabel = 'Rows';
  @Input() addLabel = '+ Filter';
  @Input() testIdPrefix = 'record-filter';
  @Input() advancedNote = 'Every filter folds through the And/Or above. Filters are client-side on the loaded rows.';
  @Input() saveHint = 'Saves the current filters to this browser.';

  @Output() conditionsChange = new EventEmitter<FilterCondition[]>();
  @Output() joinModeChange = new EventEmitter<FilterJoinMode>();
  /** Any change that should re-run the host's filter stack. */
  @Output() filterChange = new EventEmitter<RecordFilterChange>();
  @Output() viewApplied = new EventEmitter<RecordFilterViewApplied>();
  @Output() savedViewsChange = new EventEmitter<SavedFilterView[]>();
  @Output() clearAll = new EventEmitter<void>();

  savedViews: SavedFilterView[] = [];
  openConditionId: string | null = null;
  isAdvancedFilter = false;
  showSavedViews = false;
  newViewName = '';
  notice = '';

  ngOnChanges (changes: SimpleChanges): void {
    if (changes.presetStore) {
      this.savedViews = this.presetStore ? this.presetStore.load() : [];
    }
  }

  /* ── Conditions ─────────────────────────────────────────────────────── */

  /** A new chip picks nothing for the leader — field and range type are both blank. */
  addCondition (): void {
    const condition: FilterCondition = {id: newConditionId(), field: null, op: null, value: null, value2: null};
    this.conditions = [...(this.conditions || []), condition];
    this.openConditionId = condition.id;
    this.showSavedViews = false;
    this.emitConditions();
  }

  removeCondition (condition: FilterCondition): void {
    this.conditions = (this.conditions || []).filter(c => c.id !== condition.id);
    if (this.openConditionId === condition.id) this.openConditionId = null;
    this.emitConditions();
  }

  toggleConditionEditor (id: string): void {
    this.openConditionId = this.openConditionId === id ? null : id;
    this.showSavedViews = false;
  }

  closePopovers (): void {
    this.openConditionId = null;
    this.showSavedViews = false;
  }

  fieldOf (condition: FilterCondition): FilterFieldDef | undefined {
    return fieldById(this.fields, condition.field);
  }

  opDefOf (condition: FilterCondition): ConditionOpDef | null {
    return condition.op ? CONDITION_OPS[condition.op] || null : null;
  }

  /** Range types the chosen field supports — empty until a field is picked. */
  opsFor (condition: FilterCondition): ConditionOpDef[] {
    const field = this.fieldOf(condition);
    return field ? field.ops.map(id => CONDITION_OPS[id]).filter(Boolean) : [];
  }

  enumOptions (condition: FilterCondition): FilterOption[] {
    return optionsForField(this.fieldOf(condition), this.ctx);
  }

  /** Changing the field invalidates the range type, so both are re-asked. */
  setConditionField (condition: FilterCondition, fieldId: string): void {
    condition.field = fieldId || null;
    condition.op = null;
    condition.value = null;
    condition.value2 = null;
    const field = this.fieldOf(condition);
    // Single-op fields (flags) do not need a second question.
    if (field && field.ops.length === 1) condition.op = field.ops[0];
    this.emitConditions();
  }

  setConditionOp (condition: FilterCondition, op: ConditionOp): void {
    const previous = this.opDefOf(condition);
    condition.op = op || null;
    const next = this.opDefOf(condition);
    const field = this.fieldOf(condition);
    // Only clear the arguments when the new range type takes a different shape.
    if (!next || !previous || next.args !== previous.args) {
      if (!next || next.args < 2) condition.value2 = null;
      if (!next || next.args === 0) condition.value = null;
    }
    // Enum any-of takes a list; is / is not take one value.
    if (field && field.type === 'enum') {
      if (op === 'isAnyOf' && !Array.isArray(condition.value)) condition.value = condition.value == null ? [] : [condition.value];
      if (op !== 'isAnyOf' && Array.isArray(condition.value)) condition.value = condition.value[0] ?? null;
    }
    // Date "on" takes a day, the others an instant — a value from one input is junk in the other.
    if (field && field.type === 'date' && previous && next && previous.id !== next.id
        && (previous.id === 'on' || next.id === 'on')) {
      condition.value = null;
      condition.value2 = null;
    }
    this.emitConditions();
  }

  setConditionValue (condition: FilterCondition, value: any): void {
    condition.value = (value === '' || value == null) ? null : value;
    this.emitConditions();
  }

  setConditionValue2 (condition: FilterCondition, value: any): void {
    condition.value2 = (value === '' || value == null) ? null : value;
    this.emitConditions();
  }

  setConditionList (condition: FilterCondition, value: any[]): void {
    condition.value = value || [];
    this.emitConditions();
  }

  setConditionFlag (condition: FilterCondition, value: string): void {
    condition.value = value === 'yes' ? true : value === 'no' ? false : null;
    this.emitConditions();
  }

  conditionFlagValue (condition: FilterCondition): string {
    if (condition.value === true) return 'yes';
    if (condition.value === false) return 'no';
    return '';
  }

  setJoinMode (mode: FilterJoinMode): void {
    this.joinMode = mode === 'OR' ? 'OR' : 'AND';
    this.joinModeChange.emit(this.joinMode);
    this.emitFilterChange();
  }

  isConditionActive (condition: FilterCondition): boolean {
    return isConditionComplete(condition, this.fields);
  }

  conditionChipLabel (condition: FilterCondition): string {
    const field = this.fieldOf(condition);
    return field ? field.label : 'Select a field…';
  }

  /** Chip tail: what is still missing, or the range once the condition is complete. */
  conditionChipValue (condition: FilterCondition): string {
    const field = this.fieldOf(condition);
    if (!field) return '';
    const op = this.opDefOf(condition);
    if (!op) return 'choose range…';
    if (this.isConditionActive(condition)) {
      return describeCondition(condition, this.fields, {labelFor: (fieldId, value) => this.labelFor(fieldId, value)})
        .replace(`${field.label} `, '');
    }
    return `${op.symbol} …`;
  }

  conditionUnit (condition: FilterCondition): string {
    const field = this.fieldOf(condition);
    return field && field.unit === 'percent' ? '%' : '';
  }

  conditionArgCount (condition: FilterCondition): number {
    const op = this.opDefOf(condition);
    return op ? op.args : 0;
  }

  /** Date inputs: `on` wants a calendar day, everything else an instant. */
  dateInputType (condition: FilterCondition): 'date' | 'datetime-local' {
    return condition.op === 'on' ? 'date' : 'datetime-local';
  }

  /**
   * Conjunction cell in the advanced panel: `Where` leads and the join picker sits on
   * the second row. Every condition folds through the same join, so rows beyond the
   * second only echo it.
   */
  advancedConjunction (index: number): 'where' | 'select' | 'join' {
    if (index === 0) return 'where';
    return index === 1 ? 'select' : 'join';
  }

  get hasActiveFilters (): boolean {
    return (this.conditions || []).length > 0 || this.hasExternalFilter;
  }

  get activeConditionCount (): number {
    return completeConditions(this.conditions, this.fields).length;
  }

  /** Human-readable expression for the criteria line. */
  get expression (): string {
    return describeConditions(this.conditions, this.joinMode, this.fields, {labelFor: (fieldId, value) => this.labelFor(fieldId, value)});
  }

  clearAllFilters (): void {
    this.conditions = [];
    this.joinMode = 'AND';
    this.notice = '';
    this.closePopovers();
    this.joinModeChange.emit(this.joinMode);
    this.emitConditions();
    this.clearAll.emit();
  }

  /* ── Saved views ────────────────────────────────────────────────────── */

  toggleSavedViews (): void {
    this.showSavedViews = !this.showSavedViews;
    this.openConditionId = null;
  }

  get canSaveCurrentView (): boolean {
    return !!this.presetStore && !!this.newViewName.trim() && this.activeConditionCount > 0;
  }

  /** True when saving would overwrite an existing view of the same name. */
  get saveWouldReplace (): boolean {
    const key = this.newViewName.trim().toLowerCase();
    return !!key && this.savedViews.some(v => (v.name || '').trim().toLowerCase() === key);
  }

  saveCurrentView (): void {
    if (!this.canSaveCurrentView) return;
    const result = upsertSavedFilterView(this.savedViews, {
      name: this.newViewName,
      joinMode: this.joinMode,
      conditions: completeConditions(this.conditions, this.fields),
      columns: this.extraState || [],
      surface: this.surface || undefined,
    });
    this.savedViews = result.views;
    this.presetStore.save(this.savedViews);
    this.savedViewsChange.emit(this.savedViews);
    this.newViewName = '';
    this.notice = result.replaced
      ? `Updated “${result.view.name}” (v${result.view.version}).`
      : `Saved “${result.view.name}”.`;
  }

  applySavedView (view: SavedFilterView): void {
    const applied = resolveSavedFilterView(view, this.fields, this.availableItemIds);
    this.conditions = applied.conditions.map(c => ({...c, id: newConditionId()}));
    this.joinMode = view.joinMode === 'OR' ? 'OR' : 'AND';
    this.showSavedViews = false;
    this.openConditionId = null;
    this.notice = describeAppliedView(view, applied);
    this.joinModeChange.emit(this.joinMode);
    this.conditionsChange.emit(this.conditions);
    this.viewApplied.emit({
      view,
      conditions: this.conditions,
      joinMode: this.joinMode,
      droppedItemIds: applied.droppedItemIds,
      droppedFieldIds: applied.droppedFieldIds,
    });
    this.emitFilterChange();
  }

  deleteSavedView (view: SavedFilterView): void {
    this.savedViews = removeSavedFilterView(this.savedViews, view.id);
    this.presetStore.save(this.savedViews);
    this.savedViewsChange.emit(this.savedViews);
    this.notice = `Deleted “${view.name}”.`;
  }

  describeSavedView (view: SavedFilterView): string {
    const text = describeConditions(view.conditions || [], view.joinMode || 'AND', this.fields, {labelFor: (fieldId, value) => this.labelFor(fieldId, value)});
    return view.version && view.version > 1 ? `${text} · v${view.version}` : text;
  }

  /* ── Internals ──────────────────────────────────────────────────────── */

  itemLabel (id: number): string {
    const option = (this.itemOptions || []).find(o => +o.id === +id);
    return option ? option.label : String(id);
  }

  private labelFor (fieldId: string, value: string | number): string {
    const field = fieldById(this.fields, fieldId);
    if (!field) return String(value);
    if (field.type === 'items') return this.itemLabel(Number(value));
    const option = optionsForField(field, this.ctx).find(o => String(o.value) === String(value));
    return option ? option.label : String(value);
  }

  private emitConditions (): void {
    this.conditionsChange.emit(this.conditions);
    this.emitFilterChange();
  }

  private emitFilterChange (): void {
    this.filterChange.emit({conditions: this.conditions, joinMode: this.joinMode});
  }
}
